import React, { useEffect, useState } from 'react';
import { X, Calendar, History, MessageSquare, Eye, Download, FileText, MessageCircle, Clock, User, ShoppingCart, RefreshCw } from 'lucide-react';
import { useCurrency } from '../contexts/CurrencyContext';
import { getDisplayPhoneNumber } from '../utils/numberMasking';
import { tokenStorage } from '../utils/tokenStorage';
import axios from 'axios';
import './QuotationViewModal.css';

// Automatically construct API base URL safely, avoiding double /api
const getApiBaseUrl = () => {
  const envUrl = process.env.REACT_APP_API_URL || 'http://localhost:5000/api';
  return envUrl;
};

const API = getApiBaseUrl();

const QuotationViewModal = ({ 
  isOpen, 
  onClose, 
  quotation, 
  loading = false, 
  versionHistory = [],
  mode = 'details' // 'details' | 'actions'
}) => {
  const { formatAmount } = useCurrency();
  const [whatsappMsg, setWhatsappMsg] = useState({ type: '', text: '' });
  const [whatsappEnabled, setWhatsappEnabled] = useState(false);
  const [loadingAction, setLoadingAction] = useState(null); // 'whatsapp' | 'preview' | 'download'

  // Reset message when modal opens/closes
  useEffect(() => {
    if (!isOpen) {
      setWhatsappMsg({ type: '', text: '' });
      setLoadingAction(null);
    } else {
      fetchRequirements();
    }
  }, [isOpen]);

  const fetchRequirements = async () => {
    try {
      const token = tokenStorage.getApiAccessToken();
      const res = await axios.get(`${API}/requirements`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (res.data?.success) {
        setWhatsappEnabled(res.data.data?.whatsapp_quotations || false);
      }
    } catch (err) {
      console.error('Error fetching requirements:', err);
    }
  };

  const handleSendWhatsApp = async () => {
    if (!quotation?.id) return;
    setLoadingAction('whatsapp');
    setWhatsappMsg({ type: '', text: '' });
    try {
      const token = tokenStorage.getApiAccessToken();
      const res = await axios.post(
        `${API}/quotations/${quotation.id}/send-whatsapp`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      if (res.data?.success) {
        setWhatsappMsg({ type: 'success', text: '✅ ' + (res.data.message || 'Sent successfully!') });
      } else {
        setWhatsappMsg({ type: 'error', text: '❌ ' + (res.data?.message || 'Failed to send') });
      }
    } catch (err) {
      const msg = err.response?.data?.message || err.message || 'Failed to send WhatsApp message';
      setWhatsappMsg({ type: 'error', text: '❌ ' + msg });
    } finally {
      setLoadingAction(null);
      setTimeout(() => setWhatsappMsg({ type: '', text: '' }), 10000);
    }
  };

  const handlePreviewPdf = async () => {
    if (!quotation?.id) return;
    setLoadingAction('preview');
    setWhatsappMsg({ type: '', text: '' });
    try {
      const token = tokenStorage.getApiAccessToken();
      const response = await axios.get(`${API}/quotations/${quotation.id}/pdf`, {
        headers: { Authorization: `Bearer ${token}` },
        responseType: 'blob'
      });
      const url = window.URL.createObjectURL(new Blob([response.data], { type: 'application/pdf' }));
      window.open(url, '_blank');
    } catch (err) {
      console.error('PDF Preview error:', err);
      setWhatsappMsg({ type: 'error', text: '❌ Failed to generate PDF preview' });
    } finally {
      setLoadingAction(null);
    }
  };

  const handleDownloadPdf = async () => {
    if (!quotation?.id) return;
    setLoadingAction('download');
    setWhatsappMsg({ type: '', text: '' });
    try {
      const token = tokenStorage.getApiAccessToken();
      const qNum = quotation.quotation_number || 'Quotation';
      const response = await axios.get(`${API}/quotations/${quotation.id}/pdf`, {
        headers: { Authorization: `Bearer ${token}` },
        responseType: 'blob'
      });
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `${qNum}.pdf`);
      document.body.appendChild(link);
      link.click();
      link.remove();
    } catch (err) {
      console.error('PDF Download error:', err);
      setWhatsappMsg({ type: 'error', text: '❌ Failed to download PDF' });
    } finally {
      setLoadingAction(null);
    }
  };

  if (!isOpen || !quotation) return null;

  const formatCurrency = (amount) => formatAmount(amount);

  const formatDate = (dateStr) => {
    if (!dateStr || dateStr === '-') return '-';
    try {
      return new Date(dateStr).toLocaleDateString('en-IN', { year: 'numeric', month: 'short', day: 'numeric' });
    } catch {
      return dateStr;
    }
  };

  const getStatusClass = (status) => {
    const map = {
      'draft': 'qvm-draft', 'sent': 'qvm-sent', 'accepted': 'qvm-accepted',
      'rejected': 'qvm-rejected', 'expired': 'qvm-expired', 'converted': 'qvm-converted'
    };
    return map[status] || 'qvm-draft';
  };

  // Helper variables
  const qNumber = quotation.quotation_number || quotation.quotationNumber || 'N/A';
  const qStatus = quotation.status || 'draft';
  const qVersion = quotation.version || 1;
  const isLatest = quotation.is_latest;
  const customerName = quotation.customer_name || quotation.customerName || '-';
  const total = parseFloat(quotation.total_amount || quotation.totalAmount || 0);
  const quotationDate = quotation.quotation_date || quotation.quotationDate || quotation.created_at || '';
  const createdAt = quotation.created_at || '';

  // Data mapping for detail sections
  const customer = {
    name: customerName,
    email: quotation.customer_email || quotation.customerEmail || '-',
    phone: quotation.customer_phone || quotation.customerPhone || '-',
    address: quotation.customer_address || quotation.customerAddress || '-',
    city: quotation.customer_city || quotation.customerCity || '-',
    state: quotation.customer_state || quotation.customerState || '-',
    pincode: quotation.customer_pincode || quotation.customerPincode || '-'
  };

  const addresses = {
    shipping: quotation.shipping_address || quotation.shippingAddress || '-',
    billing: quotation.billing_address || quotation.billingAddress || '-'
  };

  const details = {
    date: quotationDate,
    validUntil: quotation.valid_until || quotation.validUntil || '-',
    terms: quotation.payment_terms || quotation.paymentTerms || '-'
  };

  const items = Array.isArray(quotation.items) ? quotation.items : [];
  const projectStructure = Array.isArray(quotation.project_structure) ? quotation.project_structure : [];
  const isProjectQuotation = quotation.quotation_type === 'projects';
  const showProjectStructure = isProjectQuotation && projectStructure.length > 0;

  const financial = {
    subtotal: parseFloat(quotation.subtotal || 0),
    taxAmount: parseFloat(quotation.tax_amount || 0),
    discountAmount: parseFloat(quotation.discount_amount || 0),
    shipping: parseFloat(quotation.shipping_amount || 0),
    total: total
  };

  const notes = quotation.notes || quotation.customer_notes || '';

  if (loading) {
    return (
      <div className="qvm-overlay" onClick={onClose}>
        <div className="qvm-modal" onClick={(e) => e.stopPropagation()}>
          <div className="qvm-loading">
            <div className="qvm-spinner"></div>
            <p>Loading quotation...</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="qvm-overlay" onClick={onClose}>
      <div className="qvm-modal" onClick={(e) => e.stopPropagation()}>

        {/* HEADER */}
        <div className="qvm-header">
          <div className="qvm-header-left">
            <h2 className="qvm-number">{qNumber}</h2>
            <p className="qvm-subtitle">
              {mode === 'actions' ? 'Quick Actions' : 'Quotation Details'}
              {qVersion && (
                <span style={{ marginLeft: '12px', fontSize: '13px', color: 'rgba(255,255,255,0.8)' }}>
                  • v{qVersion}
                  {isLatest && <span style={{ marginLeft: '4px', color: '#10b981', fontWeight: '700' }}>✓ Latest</span>}
                </span>
              )}
            </p>
          </div>
          <div className="qvm-badges">
            <span className={`qvm-badge ${getStatusClass(qStatus)}`}>
              {qStatus.toUpperCase()}
            </span>
          </div>
          <button className="qvm-close" onClick={onClose}>
            <X size={20} />
          </button>
        </div>

        {/* SCROLLABLE CONTENT */}
        <div className="qvm-content">

          {/* ACTION CARD (COMMAND CENTER) - GATED BY MODE */}
          {mode === 'actions' && (
            <div className="qvm-summary-card">
              <div className="qvm-summary-header">
                <div className="qvm-summary-info">
                  <h4>{qNumber}</h4>
                  <div className="qvm-summary-meta">
                    <div className="qvm-summary-meta-item">
                      <Calendar size={14} />
                      {quotationDate ? new Date(quotationDate).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' }) : '-'}
                    </div>
                    <div className="qvm-summary-meta-item">
                      <Clock size={14} />
                      {createdAt ? new Date(createdAt).toLocaleTimeString('en-IN', { hour: '2-digit', minute: '2-digit' }) : '-'}
                    </div>
                  </div>
                </div>
                <div className="qvm-summary-amount">
                  {formatCurrency(total)}
                </div>
              </div>

              <div className="qvm-summary-details">
                <span className="qvm-status-card">{qStatus.toUpperCase()}</span>
                <div className="qvm-summary-meta-item" style={{ fontSize: '12px' }}>
                  <FileText size={14} />
                  Quotation for {customerName}
                </div>
              </div>

              <div className="qvm-summary-actions">
                {whatsappEnabled && (
                  <button
                    onClick={handleSendWhatsApp}
                    className="qvm-summary-btn qvm-summary-btn-whatsapp"
                    disabled={loadingAction !== null}
                  >
                    {loadingAction === 'whatsapp' ? (
                      <>
                        <div className="qvm-summary-btn-spinner"></div>
                        Sending...
                      </>
                    ) : (
                      <>
                        <MessageSquare size={16} />
                        WhatsApp
                      </>
                    )}
                  </button>
                )}

                <button
                  onClick={handlePreviewPdf}
                  className="qvm-summary-btn qvm-summary-btn-preview"
                  disabled={loadingAction !== null}
                >
                  {loadingAction === 'preview' ? (
                    <div className="qvm-summary-btn-spinner"></div>
                  ) : <Eye size={16} />}
                  Preview
                </button>

                <button
                  onClick={handleDownloadPdf}
                  className="qvm-summary-btn qvm-summary-btn-download"
                  disabled={loadingAction !== null}
                >
                  {loadingAction === 'download' ? (
                    <div className="qvm-summary-btn-spinner"></div>
                  ) : <Download size={16} />}
                  Download
                </button>
              </div>

              {/* Status message inside the card */}
              {whatsappMsg.text && (
                <div style={{
                  padding: '10px 14px',
                  borderRadius: '6px',
                  fontSize: '13px',
                  fontWeight: '600',
                  backgroundColor: whatsappMsg.type === 'success' ? '#ecfdf5' : '#fef2f2',
                  color: whatsappMsg.type === 'success' ? '#065f46' : '#991b1b',
                  marginTop: '10px',
                  border: `1.5px solid ${whatsappMsg.type === 'success' ? '#10b981' : '#ef4444'}`,
                  display: 'flex',
                  alignItems: 'center',
                  gap: '8px'
                }}>
                  {whatsappMsg.type === 'success' ? <MessageCircle size={14} /> : <X size={14} />}
                  {whatsappMsg.text}
                </div>
              )}
            </div>
          )}

          {/* DETAIL SECTIONS - GATED BY MODE */}
          {mode === 'details' && (
            <>
              {/* VERSION HISTORY */}
              {versionHistory && versionHistory.length > 1 && (
                <section className="qvm-section">
                  <div className="qvm-section-header">
                    <History size={18} className="qvm-icon" />
                    <h3>Version History ({versionHistory.length})</h3>
                  </div>
                  <div className="qvm-section-body">
                    <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
                      {versionHistory.map((v) => (
                        <div
                          key={v.id}
                          style={{
                            padding: '8px 12px',
                            backgroundColor: v.is_latest ? '#e0e7ff' : '#f3f4f6',
                            borderRadius: '6px',
                            fontSize: '12px',
                            border: v.is_latest ? '2px solid #667eea' : '1px solid #d1d5db',
                            color: v.is_latest ? '#667eea' : '#6b7280',
                            fontWeight: '600',
                            cursor: 'default'
                          }}
                        >
                          <strong>v{v.version}</strong> - {v.quotation_number}
                          {v.is_latest && <span style={{ marginLeft: '6px', color: '#10b981' }}>✓</span>}
                        </div>
                      ))}
                    </div>
                  </div>
                </section>
              )}

              {/* CUSTOMER SECTION */}
              <section className="qvm-section">
                <div className="qvm-section-header">
                  <User size={18} className="qvm-icon" />
                  <h3>Customer Information</h3>
                </div>
                <div className="qvm-section-body">
                  <div className="qvm-grid-2">
                    <div className="qvm-field">
                      <label>Customer Name</label>
                      <div className="qvm-value">{customer.name !== '-' ? customer.name : <span className="qvm-empty">-</span>}</div>
                    </div>
                    <div className="qvm-field">
                      <label>Email</label>
                      <div className="qvm-value">
                        {customer.email !== '-' ? <a href={`mailto:${customer.email}`} className="qvm-link">{customer.email}</a> : <span className="qvm-empty">-</span>}
                      </div>
                    </div>
                    <div className="qvm-field">
                      <label>Phone</label>
                      <div className="qvm-value">
                        {customer.phone !== '-' ? <span className="qvm-link">{getDisplayPhoneNumber(customer.phone)}</span> : <span className="qvm-empty">-</span>}
                      </div>
                    </div>
                    <div className="qvm-field">
                      <label>City</label>
                      <div className="qvm-value">{customer.city !== '-' ? customer.city : <span className="qvm-empty">-</span>}</div>
                    </div>
                    <div className="qvm-field">
                      <label>State</label>
                      <div className="qvm-value">{customer.state !== '-' ? customer.state : <span className="qvm-empty">-</span>}</div>
                    </div>
                    <div className="qvm-field">
                      <label>Pincode</label>
                      <div className="qvm-value">{customer.pincode !== '-' ? customer.pincode : <span className="qvm-empty">-</span>}</div>
                    </div>
                  </div>
                  <div className="qvm-field qvm-fullwidth">
                    <label>Billing Address</label>
                    <div className="qvm-value qvm-address">{addresses.billing !== '-' ? addresses.billing : <span className="qvm-empty">-</span>}</div>
                  </div>
                </div>
              </section>

              {/* ROLE TRACKING SECTION */}
              {(quotation.created_by_role_name || quotation.assigned_by_role_name) && (
                <section className="qvm-section">
                  <div className="qvm-section-header">
                    <User size={18} className="qvm-icon" />
                    <h3>Role Information</h3>
                  </div>
                  <div className="qvm-section-body">
                    <div className="qvm-grid-2">
                      {quotation.created_by_role_name && (
                        <div className="qvm-field">
                          <label>Created By</label>
                          <div className="qvm-value">
                            <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                              <span style={{ 
                                backgroundColor: '#dbeafe', 
                                color: '#1e40af', 
                                padding: '4px 10px', 
                                borderRadius: '6px', 
                                fontSize: '13px', 
                                fontWeight: '600' 
                              }}>
                                {quotation.created_by_role_name}
                              </span>
                              {quotation.created_by_username && (
                                <span style={{ fontSize: '12px', color: '#6b7280' }}>
                                  @{quotation.created_by_username}
                                </span>
                              )}
                            </div>
                          </div>
                        </div>
                      )}
                      {quotation.assigned_by_role_name && (
                        <div className="qvm-field">
                          <label>Lead Assigned By</label>
                          <div className="qvm-value">
                            <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                              <span style={{ 
                                backgroundColor: '#fef3c7', 
                                color: '#92400e', 
                                padding: '4px 10px', 
                                borderRadius: '6px', 
                                fontSize: '13px', 
                                fontWeight: '600' 
                              }}>
                                {quotation.assigned_by_role_name}
                              </span>
                              {quotation.assigned_by_username && (
                                <span style={{ fontSize: '12px', color: '#6b7280' }}>
                                  @{quotation.assigned_by_username}
                                </span>
                              )}
                            </div>
                          </div>
                        </div>
                      )}
                    </div>
                  </div>
                </section>
              )}

              {/* ITEMS SECTION */}
              <section className="qvm-section">
                <div className="qvm-section-header">
                  <ShoppingCart size={18} className="qvm-icon" />
                  <h3>Selected Items ({items.length}){quotation.is_subscription && <span style={{ marginLeft: '8px', fontSize: '12px', backgroundColor: '#dbeafe', color: '#1e40af', padding: '2px 8px', borderRadius: '4px', fontWeight: '600' }}>SUBSCRIPTION</span>}</h3>
                </div>
                <div className="qvm-section-body qvm-items-body">
                  {showProjectStructure ? (
                    <div className="qvm-project-structure">
                      {projectStructure.map((project, projectIndex) => (
                        <div key={project.project_template_id || projectIndex} className="qvm-project-card">
                          <div className="qvm-project-title">
                            {project.project_template_name || 'Project'}
                          </div>
                          {(project.phases || []).map((phase, phaseIndex) => (
                            <div key={phase.project_template_phase_id || phaseIndex} className="qvm-phase-card">
                              <div className="qvm-phase-header">
                                <div className="qvm-phase-title">
                                  {phase.project_phase_name}
                                </div>
                                {phase.phase_duration_days ? (
                                  <div className="qvm-phase-duration">
                                    {phase.phase_duration_days} days
                                  </div>
                                ) : null}
                              </div>
                              {phase.items && phase.items.length > 0 ? (
                                <div className="qvm-table-wrapper">
                                  <table className="qvm-table">
                                    <thead>
                                      <tr>
                                        <th>Item Name</th>
                                        <th>Category</th>
                                        {quotation.is_subscription && <th>Subscription Type</th>}
                                        <th className="qvm-right">Qty</th>
                                        <th className="qvm-right">Unit Price</th>
                                        <th className="qvm-right">GST Rate</th>
                                        <th className="qvm-right">Total (excl. GST)</th>
                                      </tr>
                                    </thead>
                                    <tbody>
                                      {phase.items.map((item, idx) => {
                                        const qty = parseFloat(item.quantity || 0);
                                        const unitPrice = parseFloat(item.unit_price || 0);
                                        const baseTotal = qty * unitPrice;
                                        const gstRate = parseFloat(item.product_gst_rate || item.gst_rate || 0);
                                        const itemGST = (baseTotal * gstRate) / 100;
                                        const name = item.item_name || 'Item';

                                        const subType = item.subscription_type || 'daily';
                                        const dayQtyMap = {
                                          mon: parseFloat(item.mon || 0),
                                          tue: parseFloat(item.tue || 0),
                                          wed: parseFloat(item.wed || 0),
                                          thu: parseFloat(item.thu || 0),
                                          fri: parseFloat(item.fri || 0),
                                          sat: parseFloat(item.sat || 0),
                                          sun: parseFloat(item.sun || 0)
                                        };
                                        const hasDayWiseQty = Object.values(dayQtyMap).some(v => v > 0);

                                        return (
                                          <tr key={idx}>
                                            <td className="qvm-bold">{name}</td>
                                            <td>
                                              {item.item_details?.category || item.category ? (
                                                <span className="qvm-category-badge">
                                                  {item.item_details?.category || item.category}
                                                </span>
                                              ) : (
                                                <span className="qvm-empty">-</span>
                                              )}
                                            </td>
                                            {quotation.is_subscription && (
                                              <td>
                                                <div className="qvm-subscription-cell">
                                                  <div className="qvm-subscription-type">{subType.replace('_', ' ')}</div>
                                                  {subType === 'daily' && hasDayWiseQty && (
                                                    <div className="qvm-subscription-details">
                                                      {['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'].map((day, i) => {
                                                        const keys = ['mon', 'tue', 'wed', 'thu', 'fri', 'sat', 'sun'];
                                                        const dayQty = dayQtyMap[keys[i]];
                                                        return dayQty > 0 ? <span key={day} className="qvm-subscription-day">{day}:{dayQty}</span> : null;
                                                      })}
                                                    </div>
                                                  )}
                                                  {subType === 'alternate_days' && item.repeat_cycle && (
                                                    <div className="qvm-subscription-details">Every {item.repeat_cycle} days</div>
                                                  )}
                                                </div>
                                              </td>
                                            )}
                                            <td className="qvm-right qvm-bold">{qty.toFixed(2)}</td>
                                            <td className="qvm-right qvm-bold">{formatCurrency(unitPrice)}</td>
                                            <td className="qvm-right qvm-gst-cell">
                                              {gstRate.toFixed(2)}%
                                              <span className="qvm-gst-amount">{formatCurrency(itemGST)}</span>
                                            </td>
                                            <td className="qvm-right qvm-bold qvm-primary">{formatCurrency(baseTotal)}</td>
                                          </tr>
                                        );
                                      })}
                                    </tbody>
                                  </table>
                                </div>
                              ) : (
                                <div className="qvm-empty-state qvm-empty-phase">
                                  <p>No items in this phase</p>
                                </div>
                              )}
                            </div>
                          ))}
                        </div>
                      ))}
                    </div>
                  ) : items.length > 0 ? (
                    <div className="qvm-table-wrapper">
                      <table className="qvm-table">
                        <thead>
                            <tr>
                              <th>Item Name</th>
                              <th>Category</th>
                              {quotation.is_subscription && <th>Subscription Type</th>}
                              <th className="qvm-right">Qty</th>
                              <th className="qvm-right">Unit Price</th>
                              <th className="qvm-right">GST Rate</th>
                              <th className="qvm-right">{quotation.is_subscription ? 'Total (excl. GST)' : 'Total Price'}</th>
                            </tr>
                          </thead>
                        <tbody>
                          {items.map((item, idx) => {
                            const qty = parseFloat(item.quantity || 0);
                            const unitPrice = parseFloat(item.unit_price || 0);
                            const baseTotal = qty * unitPrice; // Total without GST
                            const gstRate = parseFloat(item.product_gst_rate || item.gst_rate || 0);
                            const itemGST = (baseTotal * gstRate) / 100;
                            const name = item.item_name || 'Item';

                            // Subscription details
                            const subType = item.subscription_type || 'daily';
                            const dayQtyMap = {
                              mon: parseFloat(item.mon || 0),
                              tue: parseFloat(item.tue || 0),
                              wed: parseFloat(item.wed || 0),
                              thu: parseFloat(item.thu || 0),
                              fri: parseFloat(item.fri || 0),
                              sat: parseFloat(item.sat || 0),
                              sun: parseFloat(item.sun || 0)
                            };
                            const hasDayWiseQty = Object.values(dayQtyMap).some(v => v > 0);

                            return (
                             <tr key={idx}>
                                <td className="qvm-bold">{name}</td>
                                <td>
                                  {item.item_details?.category || item.category ? (
                                    <span style={{ display: 'inline-flex', alignItems: 'center', gap: '4px', fontSize: '11px', fontWeight: '600', color: '#6366f1', background: '#ede9fe', padding: '2px 8px', borderRadius: '20px' }}>
                                      {item.item_details?.category || item.category}
                                    </span>
                                  ) : (
                                    <span className="qvm-empty">-</span>
                                  )}
                                </td>
                                {quotation.is_subscription && (
                                  <td>
                                    <div style={{ fontSize: '11px' }}>
                                      <div style={{ fontWeight: '600', color: '#6366f1', textTransform: 'capitalize' }}>{subType.replace('_', ' ')}</div>
                                      {subType === 'daily' && hasDayWiseQty && (
                                        <div style={{ fontSize: '10px', color: '#6b7280', marginTop: '2px' }}>
                                          {['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'].map((day, i) => {
                                            const keys = ['mon', 'tue', 'wed', 'thu', 'fri', 'sat', 'sun'];
                                            const qty = dayQtyMap[keys[i]];
                                            return qty > 0 ? <span key={day} style={{ marginRight: '4px' }}>{day}:{qty}</span> : null;
                                          })}
                                        </div>
                                      )}
                                      {subType === 'alternate_days' && item.repeat_cycle && (
                                        <div style={{ fontSize: '10px', color: '#6b7280' }}>Every {item.repeat_cycle} days</div>
                                      )}
                                    </div>
                                  </td>
                                )}
                                <td className="qvm-right qvm-bold">{qty.toFixed(2)}</td>
                                <td className="qvm-right qvm-bold">{formatCurrency(unitPrice)}</td>
                                <td className="qvm-right qvm-gst-cell">
                                  {gstRate.toFixed(2)}%
                                  <span className="qvm-gst-amount">{formatCurrency(itemGST)}</span>
                                </td>
                                <td className="qvm-right qvm-bold qvm-primary">{formatCurrency(baseTotal)}</td>
                              </tr>
                            );
                          })}
                        </tbody>
                      </table>
                    </div>
                  ) : (
                    <div className="qvm-empty-state">
                      <p>No items in this quotation</p>
                    </div>
                  )}
                </div>
              </section>

              {/* DETAILS SECTION */}
              <section className="qvm-section">
                <div className="qvm-section-header">
                  <Calendar size={18} className="qvm-icon" />
                  <h3>Quotation Details</h3>
                </div>
                <div className="qvm-section-body">
                  <div className="qvm-grid-2">
                    <div className="qvm-field">
                      <label>Quotation Date</label>
                      <div className="qvm-value">{formatDate(details.date)}</div>
                    </div>
                    <div className="qvm-field">
                      <label>Valid Until</label>
                      <div className="qvm-value">{formatDate(details.validUntil)}</div>
                    </div>
                    <div className="qvm-field">
                      <label>Payment Terms</label>
                      <div className="qvm-value">{details.terms !== '-' ? details.terms : <span className="qvm-empty">-</span>}</div>
                    </div>
                  </div>

                  {/* SUBSCRIPTION CONFIGURATION */}
                  {quotation.is_subscription && quotation.subscription_config && (() => {
                    const subConfig = typeof quotation.subscription_config === 'string' 
                      ? JSON.parse(quotation.subscription_config) 
                      : quotation.subscription_config;
                    
                    return (
                      <div style={{
                        marginTop: '20px',
                        padding: '16px',
                        background: 'linear-gradient(135deg, #ecfdf5 0%, #d1fae5 100%)',
                        border: '2px solid #10b981',
                        borderRadius: '8px'
                      }}>
                        <h4 style={{ margin: '0 0 12px 0', fontSize: '14px', fontWeight: '700', color: '#065f46', display: 'flex', alignItems: 'center', gap: '6px' }}>
                          <RefreshCw size={16} />
                          Subscription Configuration
                        </h4>
                        <div className="qvm-grid-2">
                          {subConfig.startDate && (
                            <div className="qvm-field">
                              <label>Start Date</label>
                              <div className="qvm-value" style={{ fontWeight: '600', color: '#059669' }}>{formatDate(subConfig.startDate)}</div>
                            </div>
                          )}
                          {subConfig.endDate && (
                            <div className="qvm-field">
                              <label>End Date</label>
                              <div className="qvm-value" style={{ fontWeight: '600', color: '#059669' }}>{formatDate(subConfig.endDate)}</div>
                            </div>
                          )}
                          {subConfig.deliveryTimeSlot && (
                            <div className="qvm-field">
                              <label>Delivery Time Slot</label>
                              <div className="qvm-value" style={{ textTransform: 'capitalize' }}>{subConfig.deliveryTimeSlot}</div>
                            </div>
                          )}
                          {subConfig.billingCycle && (
                            <div className="qvm-field">
                              <label>Billing Cycle</label>
                              <div className="qvm-value" style={{ textTransform: 'capitalize' }}>{subConfig.billingCycle.replace('_', ' ')}</div>
                            </div>
                          )}
                        </div>
                        {subConfig.deliveryInstructions && (
                          <div className="qvm-field qvm-fullwidth" style={{ marginTop: '12px' }}>
                            <label>Delivery Instructions</label>
                            <div className="qvm-value" style={{ fontSize: '13px', fontStyle: 'italic' }}>{subConfig.deliveryInstructions}</div>
                          </div>
                        )}
                        {subConfig.startDate && subConfig.endDate && (() => {
                          const start = new Date(subConfig.startDate);
                          const end = new Date(subConfig.endDate);
                          const days = Math.ceil((end - start) / (1000 * 60 * 60 * 24)) + 1;
                          return (
                            <div style={{ marginTop: '12px', padding: '8px 12px', background: 'rgba(16, 185, 129, 0.1)', borderRadius: '4px', fontSize: '12px', fontWeight: '600', color: '#065f46' }}>
                              ⏱ Duration: {days} days
                            </div>
                          );
                        })()}
                      </div>
                    );
                  })()}

                  {/* SHIPPING ADDRESS */}
                  {addresses.shipping !== '-' && (
                    <div className="qvm-field qvm-fullwidth" style={{ marginTop: '20px' }}>
                      <label>📦 Shipping Address</label>
                      <div className="qvm-value qvm-address">{addresses.shipping}</div>
                    </div>
                  )}

                  {/* PRICING SUMMARY */}
                  <div className="qvm-pricing">
                    <h4>💰 Pricing Summary</h4>
                    <div className="qvm-pricing-content">
                      <div className="qvm-price-row">
                        <span>Subtotal (excl. GST)</span>
                        <span className="qvm-bold">{formatCurrency(financial.subtotal)}</span>
                      </div>

                      {parseFloat(financial.taxAmount) > 0 && (
                        <div className="qvm-price-row qvm-gst-row">
                          <span>GST</span>
                          <span className="qvm-bold">{formatCurrency(financial.taxAmount)}</span>
                        </div>
                      )}

                      {parseFloat(financial.discountAmount) > 0 && (
                        <div className="qvm-price-row qvm-discount">
                          <span>Discount</span>
                          <span className="qvm-bold qvm-success">-{formatCurrency(financial.discountAmount)}</span>
                        </div>
                      )}

                      {parseFloat(financial.shipping) > 0 && (
                        <div className="qvm-price-row">
                          <span>Shipping Amount</span>
                          <span className="qvm-bold">{formatCurrency(financial.shipping)}</span>
                        </div>
                      )}

                      <div className="qvm-price-row qvm-total">
                        <span>Total Amount</span>
                        <span className="qvm-bold qvm-primary">{formatCurrency(financial.total)}</span>
                      </div>
                    </div>
                  </div>

                  {notes && (
                    <div className="qvm-field qvm-fullwidth" style={{ marginTop: '20px' }}>
                      <label>Notes</label>
                      <div className="qvm-value qvm-notes">{notes}</div>
                    </div>
                  )}
                </div>
              </section>
            </>
          )}

        </div>

        {/* FOOTER */}
        <div className="qvm-footer">
          <button className="qvm-btn" onClick={onClose}>
            ✕ Close
          </button>
        </div>
      </div>
    </div>
  );
};

export default QuotationViewModal;